import { Controller } from "@hotwired/stimulus"

// disables checkboxes whose values are already claimed by another checked checkbox,
// so an overlapping option can't be selected twice across the list.
//
// the data attribute holding the comma-separated values is configurable via the
// `key` value, e.g. data-checkbox-overlap-key-value="champ-ids" reads data-champ-ids.
export default class extends Controller {
  static values = { key: String, claimedBy: String }

  connect() {
    this.refresh()
  }

  // recompute every checkbox's disabled state from the currently checked boxes
  refresh() {
    const checkboxes = this.#checkboxes()

    // map each claimed value to the names of the checked checkboxes claiming it
    const claimants = new Map()
    checkboxes.filter(cb => cb.checked).forEach(cb => {
      this.#values(cb).forEach(value => {
        if (!claimants.has(value)) claimants.set(value, new Set())
        claimants.get(value).add(this.#name(cb))
      })
    })

    // disable any unchecked checkbox that overlaps a claimed value, re-enable the rest,
    // and name the claiming checkboxes at the right of the disabled row
    checkboxes.forEach(cb => {
      const names = cb.checked ? [] : this.#claimingNames(cb, claimants)
      cb.disabled = names.length > 0
      this.#label(cb)?.classList.toggle("disabled", cb.disabled)
      this.#renderClaimedBy(cb, this.#showsClaimedBy(cb) ? names : [])
    })
  }

  // distinct names of the checked checkboxes claiming any of this checkbox's values
  #claimingNames(checkbox, claimants) {
    const names = new Set()
    this.#values(checkbox).forEach(value => {
      claimants.get(value)?.forEach(name => names.add(name))
    })
    return Array.from(names).filter(Boolean)
  }

  // show or clear a note at the right of the row naming the checkboxes that claimed its values
  #renderClaimedBy(checkbox, names) {
    const cell = checkbox.closest("td")
    if (!cell) return

    let note = cell.querySelector(".claimed-by")
    if (names.length === 0) {
      note?.remove()
      return
    }

    if (!note) {
      note = document.createElement("span")
      note.className = "claimed-by"
      cell.appendChild(note)
    }
    note.textContent = `${this.claimedByValue} ${names.join(", ")}`.trim()
  }

  // the label associated with a checkbox, looked up by its `for` attribute,
  // falling back to a wrapping label
  #label(checkbox) {
    return (checkbox.id && this.element.querySelector(`label[for="${checkbox.id}"]`)) || checkbox.closest("label")
  }

  #name(checkbox) {
    return checkbox.dataset.name || ""
  }

  // whether this checkbox opts in to showing a claimed-by note when disabled,
  // via data-show-claimed-by="true"
  #showsClaimedBy(checkbox) {
    return checkbox.dataset.showClaimedBy === "true"
  }

  #checkboxes() {
    return Array.from(this.element.querySelectorAll(`input[type=checkbox][data-${this.keyValue}]`))
  }

  #values(checkbox) {
    return (checkbox.dataset[this.#datasetKey] || "").split(",").map(value => value.trim()).filter(Boolean)
  }

  // convert the kebab-case key value into its camelCase dataset property name
  get #datasetKey() {
    return this.keyValue.replace(/-([a-z])/g, (_, char) => char.toUpperCase())
  }
}
